import { useMutation } from '@tanstack/react-query'
import { App, Button, Flex, Form, Input, Result, Typography } from 'antd'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  apiErrorCode,
  coreStepIndex,
  CoreStep,
  OnboardingErrorField,
  track
} from 'src/analytics'
import {
  authorizationV2SignupConfirmCreateMutation,
  usersLoginAdminCreateMutation
} from 'src/api/main'
import { paths } from 'src/router'
import { useInlineApiErrors } from 'src/utils'
import { setLocalStorageToken } from 'src/utils/getToken'

import { AuthHeader, AuthLink } from '../components'
import { asAuthenticatedTokens } from '../utils/authResponseAdapters'

const { Text } = Typography

type ConfirmRegisterFormValues = {
  password: string
  confirm_password: string
}

export const ConfirmRegister = () => {
  const [form] = Form.useForm<ConfirmRegisterFormValues>()
  const { message } = App.useApp()
  const { showApiErrors, errorAlert } = useInlineApiErrors()

  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const navigate = useNavigate()
  const { t } = useTranslation()

  /** Guards the once-per-mount report against StrictMode's double invocation. */
  const reported = useRef(false)

  // No `onboarding_started` here: the journey began on the Register screen, and
  // this page is only ever reached from the link that screen sent.
  useEffect(() => {
    if (reported.current) return
    reported.current = true

    if (!token) {
      // A truncated or already-spent link is a dead end the user cannot argue
      // with, and it looks exactly like abandonment unless it is recorded.
      track('onboarding_step_error', {
        step: CoreStep.ACCOUNT,
        field: OnboardingErrorField.TOKEN,
        error_code: 'missing_token'
      })

      return
    }

    track('onboarding_step_viewed', {
      step: CoreStep.ACCOUNT,
      step_index: coreStepIndex(CoreStep.ACCOUNT)
    })
  }, [token])

  const { mutateAsync: fetchUserAfterLogin } = useMutation({
    ...usersLoginAdminCreateMutation()
  })

  const { mutateAsync: signupConfirm, isPending } = useMutation({
    ...authorizationV2SignupConfirmCreateMutation(),
    onSuccess: async (response) => {
      message.success(t('registration_success'))

      const tokens = asAuthenticatedTokens(response)
      if (!tokens) {
        // Backend didn't return a JWT pair (e.g. older deployment) — fall back
        // to the manual login screen instead of leaving the user stranded.
        navigate(paths.auth.login)
        return
      }

      setLocalStorageToken(tokens)
      try {
        await fetchUserAfterLogin({})
      } catch {
        // User profile fetch failure is non-fatal: the JWT is already stored,
        // the home page will retry the user query on mount.
      }
      navigate(paths.home)
    },
    onError: (e) => {
      // Attributed to `password` rather than `token`: a spent or invalid token
      // and a rejected password both surface here, but only the status code can
      // tell them apart, and it travels as `error_code`.
      track('onboarding_step_error', {
        step: CoreStep.ACCOUNT,
        field: OnboardingErrorField.PASSWORD,
        error_code: apiErrorCode(e)
      })
      showApiErrors(e, form)
    }
  })

  const onSubmit = async () => {
    const values = await form.validateFields()
    await signupConfirm({ body: { token, ...values } })
  }

  if (!token) {
    return (
      <Result
        status="warning"
        title={t('registration_confirm_missing_token_title')}
        subTitle={t('registration_confirm_missing_token_description')}
        extra={
          <Button type="primary" onClick={() => navigate(paths.auth.register)}>
            {t('registration')}
          </Button>
        }
      />
    )
  }

  return (
    <Flex orientation="vertical" gap={16} className="w-full">
      <AuthHeader
        title={t('registration_confirm_title')}
        subtitle={t('registration_confirm_description')}
      />

      {errorAlert}

      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        requiredMark={false}
        id="confirm-register-form"
      >
        <Flex orientation="vertical" gap={16}>
          <Flex orientation="vertical" gap={10}>
            <Form.Item
              name="password"
              label={t('password')}
              style={{ marginBottom: 0 }}
              rules={[
                { required: true },
                { min: 8, message: t('password_min_length') }
              ]}
            >
              <Input.Password
                autoFocus
                placeholder={t('input_placeholder_password')}
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirm_password"
              label={t('password_repeat')}
              dependencies={['password']}
              hasFeedback
              style={{ marginBottom: 0 }}
              rules={[
                { required: true },
                { min: 8, message: t('password_min_length') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error(t('passwords_must_match')))
                  }
                })
              ]}
            >
              <Input.Password
                placeholder={t('input_placeholder_password')}
                autoComplete="new-password"
              />
            </Form.Item>
          </Flex>

          <Button
            block
            size="large"
            onClick={onSubmit}
            type="primary"
            loading={isPending}
            form="confirm-register-form"
            htmlType="submit"
          >
            {t('submit')}
          </Button>
        </Flex>
      </Form>

      <Flex align="center" justify="center" gap={6} wrap="wrap">
        <Text type="secondary">{t('have_account')}</Text>
        <AuthLink to={paths.auth.login}>{t('sign_in')}</AuthLink>
      </Flex>
    </Flex>
  )
}
